import { COMPOSIO_INTEGRATION_DEFINITIONS } from "../constants/integration";

// NOTE: NSAI-5688 — a connector slug (an EIntegrationProvider value, e.g.
// "azure-calendar") → the i18n key for its product-facing display name, in the
// `integration` namespace (the `list.*` keys). These are the SAME labels the
// Integrations page renders, built once from the shared connector catalog so the
// run history, the Integrations page and mobile can never drift apart.
const CONNECTOR_NAME_KEY_BY_SLUG: Record<string, string> = Object.fromEntries(
  COMPOSIO_INTEGRATION_DEFINITIONS.map((definition) => [
    definition.id,
    definition.nameKey,
  ]),
);

// NOTE: NSAI-5688 — the `integration`-namespace i18n key for a connector slug, or
// null when the slug is not in the catalog (the caller then falls back to
// prettifyConnectorSlug). Never returns the raw slug itself.
export const getConnectorNameKey = (providerSlug: string): string | null =>
  CONNECTOR_NAME_KEY_BY_SLUG[providerSlug] ?? null;

export interface IBuildRunIntegrationKeyProps {
  provider: string;
  account?: string | null;
}

// NOTE: NSAI-5688 — the stable key for one run integration, mirroring the pair the
// server dedupes chips on (`provider|accountLabel`). BOTH sides of the run card use
// it — the chip (to look up its tool-call count) and the trace (to build that count
// map) — so it lives here rather than being spelled out at each site, where a drift
// would silently break the counts.
export const buildRunIntegrationKey = ({
  provider,
  account,
}: IBuildRunIntegrationKeyProps): string => `${provider}|${account ?? ""}`;

export interface IRunIntegrationFocusValue {
  provider: string;
  account: string | null;
}

// NOTE: NSAI-5688 — normalises a run integration's `{provider, account?}` into the
// focus identity the chips and the trace compare on, collapsing a missing account to
// an explicit `null`. Lives here because both platforms built the identical object by
// hand; keeping one definition stops web and mobile drifting on how "no account" is
// represented, which is what the whole match depends on.
export const toRunIntegrationFocus = ({
  provider,
  account,
}: IBuildRunIntegrationKeyProps): IRunIntegrationFocusValue => ({
  provider,
  account: account ?? null,
});

export interface IDoesStepMatchRunIntegrationFocusProps {
  step: { provider: string | null; accountLabel: string | null };
  focus: IRunIntegrationFocusValue;
}

// NOTE: NSAI-5688 — a trace step belongs to a chip when their (provider, account)
// identity matches, the same pair the server deduped the chips on. Shared because
// web and mobile both decide "is this step part of the focused connector?" and a
// drift between the two would silently highlight the wrong steps on one platform.
export const doesStepMatchRunIntegrationFocus = ({
  step,
  focus,
}: IDoesStepMatchRunIntegrationFocusProps): boolean =>
  step.provider === focus.provider &&
  (step.accountLabel ?? null) === focus.account;

// NOTE: NSAI-5688 review — the trace recomputes per-chip counts on every poll tick
// while a run is `running`, and a fresh object each time re-rendered the card and
// every chip even when the numbers were identical. Callers compare against the last
// reported map and skip the callback when nothing changed.
export const areRunIntegrationCountsEqual = (
  first: Record<string, number>,
  second: Record<string, number>,
): boolean => {
  const firstKeys = Object.keys(first);
  if (firstKeys.length !== Object.keys(second).length) {
    return false;
  }
  return firstKeys.every((key) => first[key] === second[key]);
};

// NOTE: The subset of a run step this module needs. Kept structural rather than
// importing IAgentRunStep so the helpers stay trivially testable and both platforms
// can pass their own step rows.
export interface IRunTraceStepLike {
  uuid: string;
  provider: string | null;
  accountLabel: string | null;
}

// NOTE: NSAI-5688 review (Sonar duplication) — how many tool calls each chip stands
// for, keyed exactly like the chip. Steps with no provider have no chip, so they are
// skipped. Lives here because web and mobile were each carrying a byte-identical copy.
export const buildRunIntegrationCounts = (
  steps: readonly IRunTraceStepLike[],
): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (const step of steps) {
    if (step.provider == null) {
      continue;
    }
    const key = buildRunIntegrationKey({
      provider: step.provider,
      account: step.accountLabel,
    });
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
};

export interface IRunIntegrationFocusSelection extends IRunIntegrationFocusValue {
  // NOTE: 0-based index of the match the user is currently pointing at. The caller
  // advances it on each click and drops the focus entirely once past the last match,
  // so the highlight clears rather than looping forever.
  index: number;
}

export interface IResolveRunIntegrationFocusProps {
  steps: readonly IRunTraceStepLike[];
  focus?: IRunIntegrationFocusSelection | null;
}

export interface IRunIntegrationFocusResult {
  // NOTE: The ONE step the current click points at. Only this step is highlighted —
  // highlighting every match made it impossible to see which one you were on.
  currentUuid: string | null;
  // NOTE: How many steps the focused chip covers, so the chip can render "1/2".
  matchCount: number;
}

// NOTE: NSAI-5688 review — THE shared answer to "which single step is the user
// pointing at, and how many are there?". Web and mobile previously each filtered the
// matches and repeated the index maths, which is duplicated lines and a real drift
// risk between the platforms.
export const resolveRunIntegrationFocus = ({
  steps,
  focus,
}: IResolveRunIntegrationFocusProps): IRunIntegrationFocusResult => {
  if (!focus) {
    return { currentUuid: null, matchCount: 0 };
  }
  const matches = steps.filter((step) =>
    doesStepMatchRunIntegrationFocus({ step, focus }),
  );
  if (matches.length === 0) {
    return { currentUuid: null, matchCount: 0 };
  }
  // NOTE: Clamp rather than wrap — the caller clears the focus past the last match,
  // so an out-of-range index here only happens transiently while counts load.
  const index = Math.min(Math.max(focus.index, 0), matches.length - 1);
  return {
    currentUuid: matches[index]?.uuid ?? null,
    matchCount: matches.length,
  };
};

// NOTE: NSAI-5688 — human-readable fallback for a slug with no catalog entry (e.g. a
// newer Pipedream/Composio app not yet in COMPOSIO_INTEGRATION_DEFINITIONS). Strips
// the rail prefix that means nothing to a customer ("azure-", "google-") and
// title-cases the rest, so the UI never shows a raw "azure-…" identifier.
export const prettifyConnectorSlug = (providerSlug: string): string =>
  providerSlug
    .replace(/^(azure|google)-/, "")
    .split(/[-_]/)
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
